import { Fragment, type ReactNode } from "react";

// Render Unicode enclosed numbers (① .. ⑳) as uniform inline badges.
// Native glyphs render inconsistently across fonts (e.g. ②/④ appear larger),
// so we draw our own circle sized in em units to match the surrounding text.
const isCircledNumber = (ch: string): boolean =>
  ch.length === 1 && ch >= "①" && ch <= "⑳";

const renderCircledNumbers = (text: string, keyPrefix: string): ReactNode[] => {
  const parts = text.split(/([①-⑳])/g);
  return parts.map((part, idx) => {
    if (isCircledNumber(part)) {
      const digit = part.charCodeAt(0) - 0x245f; // ① (U+2460) -> 1
      return (
        <span
          key={`${keyPrefix}-c${idx}`}
          className="inline-flex items-center justify-center w-[1.3em] h-[1.3em] shrink-0 rounded-full border border-current text-[0.7em] leading-none align-middle translate-y-[-0.06em] mx-[1px]"
        >
          {digit}
        </span>
      );
    }
    return <Fragment key={`${keyPrefix}-t${idx}`}>{part}</Fragment>;
  });
};

// Render a line of text with bold markers and circled-number badges.
// Supports both `**bold**` (markdown style) and `<b>bold</b>` (HTML style)
// so the privacy and terms pages can share one renderer.
export const renderRichText = (text: string): ReactNode[] => {
  const parts = text.split(/(\*\*[^*]+\*\*|<b>[\s\S]*?<\/b>)/g);
  return parts.flatMap((part, idx): ReactNode[] => {
    const isMd = part.startsWith("**") && part.endsWith("**");
    const isTag = part.startsWith("<b>") && part.endsWith("</b>");
    if (isMd || isTag) {
      const inner = isMd ? part.slice(2, -2) : part.slice(3, -4);
      return [
        <strong key={`b${idx}`}>
          {renderCircledNumbers(inner, `b${idx}`)}
        </strong>,
      ];
    }
    return renderCircledNumbers(part, `t${idx}`);
  });
};
